import { useEffect, useMemo, useState, type FC } from "react"

import { useLazyQuery, useMutation, useQuery } from "@apollo/client"

import KnownIssueFilterPanel, {
	type KnownIssueFilter,
} from "./KnownIssueFilterPanel"
import KnownIssueListTable from "./KnownIssueListTable"
import styles from "./KnownIssuesPage.module.scss"
import { type DaySummariesByIssueId } from "./day_summary"

import { getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"
import type { KnownIssuesParam } from "@/souhl_admin/gql/__generated__/graphql"

const KnownIssueIdFragment = graphql(/* GraphQL */ `
	fragment KnownIssueId on KnownIssue {
		id
	}
`)

const GetKnownIssues = graphql(/* GraphQL */ `
	query GetKnownIssues($param: KnownIssuesParam) {
		knownIssues(param: $param) {
			knownIssues {
				...KnownIssueId
				...KnownIssueListTable
			}
		}
	}
`)

const GetKnownIssueDaySummaries = graphql(/* GraphQL */ `
	query GetKnownIssueDaySummaries($ids: [String!]!) {
		knownIssueDaySummaries(ids: $ids) {
			summaries {
				knownIssueId
				daySummaries {
					...KnownIssueDaySummaryFragment
				}
				last24HoursMatchCount
			}
		}
	}
`)

const ResolveKnownIssues = graphql(/* GraphQL */ `
	mutation ResolveKnownIssues($input: ResolveKnownIssuesInput!) {
		resolveKnownIssues(input: $input) {
			ids
		}
	}
`)

const CloseKnownIssues = graphql(/* GraphQL */ `
	mutation CloseKnownIssues($input: CloseKnownIssuesInput!) {
		closeKnownIssues(input: $input) {
			ids
		}
	}
`)

const ReopenKnownIssues = graphql(/* GraphQL */ `
	mutation ReopenKnownIssues($input: ReopenKnownIssuesInput!) {
		reopenKnownIssues(input: $input) {
			ids
		}
	}
`)

const defaultFilter: KnownIssueFilter = {
	status: "OPEN",
	logLevel: null,
	issueType: null,
	logModuleName: null,
	logKind: null,
	logSalonId: null,
}

const KnownIssuesPage: FC = () => {
	const [filter, setFilter] = useState<KnownIssueFilter>(defaultFilter)
	const [appliedFilter, setAppliedFilter] =
		useState<KnownIssueFilter>(defaultFilter)
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
	const [confirmAction, setConfirmAction] = useState<{
		type: "resolve" | "close" | "reopen"
		ids: string[]
	} | null>(null)

	// Convert applied filter to GraphQL param (remove null values)
	const param: KnownIssuesParam = {
		...(appliedFilter.status && { status: appliedFilter.status }),
		...(appliedFilter.logLevel && { logLevel: appliedFilter.logLevel }),
		...(appliedFilter.issueType && { issueType: appliedFilter.issueType }),
		...(appliedFilter.logModuleName && {
			logModuleName: appliedFilter.logModuleName,
		}),
		...(appliedFilter.logKind && { logKind: appliedFilter.logKind }),
		...(appliedFilter.logSalonId && { logSalonId: appliedFilter.logSalonId }),
	}

	const { data, loading, refetch } = useQuery(GetKnownIssues, {
		variables: { param: Object.keys(param).length > 0 ? param : null },
		fetchPolicy: "no-cache",
	})

	const [
		fetchDaySummaries,
		{ data: daySummariesData, loading: daySummariesLoading },
	] = useLazyQuery(GetKnownIssueDaySummaries)

	// Fetch daySummaries when knownIssues change
	const knownIssueIds = useMemo(() => {
		if (!data?.knownIssues.knownIssues) return []
		return data.knownIssues.knownIssues.map(
			(issue) => getFragmentData(KnownIssueIdFragment, issue).id
		)
	}, [data])

	useEffect(() => {
		if (knownIssueIds.length > 0) {
			fetchDaySummaries({
				variables: { ids: knownIssueIds },
				fetchPolicy: "no-cache",
			})
		}
	}, [knownIssueIds, fetchDaySummaries])

	// Build daySummariesByIssueId map
	const daySummariesByIssueId = useMemo(():
		| DaySummariesByIssueId
		| undefined => {
		if (!daySummariesData?.knownIssueDaySummaries.summaries) return undefined
		const map: DaySummariesByIssueId = new Map()
		for (const entry of daySummariesData.knownIssueDaySummaries.summaries) {
			map.set(entry.knownIssueId, {
				daySummaries: entry.daySummaries,
				last24HoursMatchCount: entry.last24HoursMatchCount,
			})
		}
		return map
	}, [daySummariesData])

	const handleSearch = () => {
		setAppliedFilter(filter)
		setSelectedIds(new Set())
	}

	const [resolveKnownIssues, { loading: resolveLoading }] =
		useMutation(ResolveKnownIssues)
	const [closeKnownIssues, { loading: closeLoading }] =
		useMutation(CloseKnownIssues)
	const [reopenKnownIssues, { loading: reopenLoading }] =
		useMutation(ReopenKnownIssues)

	const isActionLoading = resolveLoading || closeLoading || reopenLoading

	const handleResolve = (ids: string[]) => {
		setConfirmAction({ type: "resolve", ids })
	}

	const handleClose = (ids: string[]) => {
		setConfirmAction({ type: "close", ids })
	}

	const handleReopen = (ids: string[]) => {
		setConfirmAction({ type: "reopen", ids })
	}

	const handleConfirm = async () => {
		if (!confirmAction) return

		try {
			if (confirmAction.type === "resolve") {
				await resolveKnownIssues({
					variables: { input: { ids: confirmAction.ids } },
				})
			} else if (confirmAction.type === "close") {
				await closeKnownIssues({
					variables: { input: { ids: confirmAction.ids } },
				})
			} else if (confirmAction.type === "reopen") {
				await reopenKnownIssues({
					variables: { input: { ids: confirmAction.ids } },
				})
			}
			setSelectedIds(new Set())
			setConfirmAction(null)
			await refetch()
		} catch (error) {
			console.error("Action failed:", error)
			alert("操作に失敗しました")
		}
	}

	const handleCancel = () => {
		setConfirmAction(null)
	}

	if (loading && !data) {
		return (
			<div className={styles.loadingContainer}>
				<div className={styles.loading}>読み込み中...</div>
			</div>
		)
	}

	const knownIssues = data?.knownIssues.knownIssues ?? []

	return (
		<div className={styles.container}>
			<KnownIssueFilterPanel
				filter={filter}
				onFilterChange={setFilter}
				onSearch={handleSearch}
			/>
			<KnownIssueListTable
				knownIssues={knownIssues}
				daySummariesByIssueId={daySummariesByIssueId}
				daySummariesLoading={daySummariesLoading}
				selectedIds={selectedIds}
				onSelectionChange={setSelectedIds}
				onResolve={handleResolve}
				onClose={handleClose}
				onReopen={handleReopen}
				appliedStatusFilter={appliedFilter.status}
				isActionLoading={isActionLoading}
			/>

			{confirmAction && (
				<div className={styles.dialogOverlay}>
					<div className={styles.dialog}>
						<h3 className={styles.dialogTitle}>確認</h3>
						<p className={styles.dialogMessage}>
							{confirmAction.ids.length} 件の Known Issue を
							{confirmAction.type === "resolve"
								? " Resolve "
								: confirmAction.type === "close"
									? " Close "
									: " Open に戻 "}
							しますか？
						</p>
						<div className={styles.dialogActions}>
							<button
								className={styles.cancelButton}
								onClick={handleCancel}
								disabled={isActionLoading}
							>
								キャンセル
							</button>
							<button
								className={styles.confirmButton}
								onClick={handleConfirm}
								disabled={isActionLoading}
							>
								{isActionLoading ? "処理中..." : "OK"}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default KnownIssuesPage
